import { ToastIcon } from '@/assets/toastIcon'
import { StatusState } from '@/components/upseat-ui/foodAndDrinks/StatusState'
import { useFnbStore } from '@/context/useFnbStore'
import useCallRouteWithDirection from '@/hooks/ui/useCallRouteWithDirection'
import {
  getOrderConfirmed,
  OrderConfirmedData,
} from '@/services/foodAndDrinks/getOrderConfirmed'
import { OrderItem } from '@/types/fnb'

import { AlertTriangle, ChevronDown, Clock, MapPin } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

// The order only flips to complete once the Stripe webhook + POS submission
// (which set `external_id` on the backend) have run. That pipeline is fully
// async — Stripe webhook delivery + Cloud Tasks queue + a live POS round-trip —
// so we poll with backoff for up to ~48s before giving up.
const POLL_DELAYS_MS = [2000, 3000, 4000, 5000, 6000, 8000, 10000]

type ConfirmationStatus = 'pending' | 'confirmed' | 'failed' | 'timeout'

export const FnBConfirmation = () => {
  const callRouteWithDirection = useCallRouteWithDirection()
  const { clearBasket } = useFnbStore()
  const { id } = useParams()

  const [order, setOrder] = useState<OrderConfirmedData | null>(null)
  const [status, setStatus] = useState<ConfirmationStatus>('pending')

  // Scrollbars are hidden globally, so show a down arrow when the item list
  // overflows its capped height (measured once the list is in the DOM).
  const [itemsScrollable, setItemsScrollable] = useState(false)
  const itemsRef = useCallback((el: HTMLUListElement | null) => {
    if (el) setItemsScrollable(el.scrollHeight > el.clientHeight)
  }, [])

  // The Stripe redirect / abandoned-basket plumbing leaves `fnbOrder` and
  // `paymentCompleted` in localStorage. Left in place, the wallet would yank a
  // completed order to `/orders/...` and fire a misleading upgrade/swap
  // "successfully" toast. Reaching this page means the F&D payment already
  // succeeded, so clear them — this screen owns the confirmation UX end-to-end.
  useEffect(() => {
    localStorage.removeItem('fnbOrder')
    localStorage.removeItem('abandonedFnbOrder')
    localStorage.removeItem('paymentCompleted')
    localStorage.removeItem('paymentCompletedAt')
    localStorage.removeItem('paymentCompletedOrder')
  }, [])

  useEffect(() => {
    // Guard against a missing or stringified-undefined id reaching the URL
    // (e.g. `.../confirmation/undefined`) — never poll a bogus uid.
    if (!id || id === 'undefined' || id === 'null') {
      callRouteWithDirection('/food-and-drinks', false, 1)
      return
    }

    let timer: ReturnType<typeof setTimeout>
    let cancelled = false

    const fetchOrder = async (attempt: number) => {
      const res = await getOrderConfirmed(id)
      if (cancelled) return

      if (res.status === 'complete') {
        if (res.data?.items?.length) {
          setOrder(res.data)
          setStatus('confirmed')
        } else {
          // Reported complete but with no items — treat as a soft failure.
          setStatus('failed')
        }
        return
      }

      if (res.status === 'failed') {
        setStatus('failed')
        return
      }

      // status === 'pending' → keep polling until we run out of delays.
      if (attempt >= POLL_DELAYS_MS.length) {
        setStatus('timeout')
        return
      }

      timer = setTimeout(() => fetchOrder(attempt + 1), POLL_DELAYS_MS[attempt])
    }

    timer = setTimeout(() => fetchOrder(0), POLL_DELAYS_MS[0])

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
    // callRouteWithDirection is unstable; depending on it re-polls every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Browser back would land on an empty menu and look like the order failed
  useEffect(() => {
    const onBack = () => callRouteWithDirection('/tickets', false, 1, true)
    window.addEventListener('popstate', onBack)
    return () => window.removeEventListener('popstate', onBack)
  }, [callRouteWithDirection])

  const goToTickets = useCallback(() => {
    clearBasket()
    callRouteWithDirection('/tickets', false, 1)
  }, [clearBasket, callRouteWithDirection])

  const goToMenu = useCallback(() => {
    clearBasket()
    callRouteWithDirection('/food-and-drinks', false, 1)
  }, [clearBasket, callRouteWithDirection])

  const ticketsButton = (variant: 'primary' | 'secondary') => (
    <button
      type="button"
      onClick={goToTickets}
      className={
        variant === 'primary'
          ? 'mt-1 rounded-full bg-purple px-6 py-3 text-sm font-semibold text-white transition-colors active:bg-purple/90'
          : 'mt-1 rounded-full border-2 border-purple px-5 py-2 text-sm font-semibold text-purple transition-colors active:bg-purple active:text-white'
      }
    >
      Return to tickets
    </button>
  )

  return (
    <main className="relative flex h-[100dvh] w-full flex-col overflow-hidden bg-white px-6 pb-10 pt-12 text-purple">
      {status === 'pending' && (
        <StatusState
          icon={
            <div className="h-9 w-9 animate-spin rounded-full border-2 border-purple border-t-transparent" />
          }
          title="Confirming your order…"
          subtitle="This usually only takes a few seconds."
        />
      )}

      {status === 'confirmed' && (
        <StatusState
          className="h-auto max-h-[100dvh] min-h-0 justify-start gap-3 py-6"
          icon={<ToastIcon />}
          title="Your order is confirmed"
          subtitle="You'll receive an email receipt shortly."
          action={ticketsButton('secondary')}
        >
          <div className="flex w-full max-w-xs shrink-0 flex-col items-center gap-2">
            {order?.order_id && (
              <p className="text-lg font-semibold">
                Order #{order.order_id.substring(0, 6).toUpperCase()}
              </p>
            )}

            {(order?.location_name || order?.collection_time) && (
              <div className="flex w-full flex-col gap-2 rounded-[20px] bg-purple7 p-3 text-left">
                {order?.location_name && (
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-purple text-yellow">
                      <MapPin size={16} strokeWidth={2.25} />
                    </span>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-wide text-purple2">
                        Collect from
                      </span>
                      <span className="text-sm font-semibold leading-tight">
                        {order.location_name}
                      </span>
                    </div>
                  </div>
                )}
                {order?.collection_time && (
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-purple text-yellow">
                      <Clock size={16} strokeWidth={2.25} />
                    </span>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-wide text-purple2">
                        Collection time
                      </span>
                      <span className="text-sm font-semibold leading-tight">
                        {order.collection_time}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {order?.items?.length ? (
            <div className="flex w-full max-w-xs shrink flex-col gap-1.5">
              <span className="text-[10px] uppercase tracking-wide text-purple2">
                Your items
              </span>
              {/* Capped height so a long order scrolls inside its own card
                  instead of pushing the page / squeezing the cards above. */}
              <ul
                ref={itemsRef}
                className="flex max-h-[26dvh] flex-col divide-y divide-grey3 overflow-y-auto rounded-[16px] bg-purple7 px-3"
              >
                {order.items.map((item: OrderItem) => (
                  <li
                    key={item.uid}
                    className="flex items-center gap-2.5 py-2 text-left"
                  >
                    <span className="shrink-0 text-xs font-semibold text-purple2">
                      {item.quantity ?? 1}x
                    </span>
                    {/* name is nullable/blank on the backend
                        (POSOrderItemReduced); fall back so we never render a
                        blank line. */}
                    <span className="text-xs font-medium leading-tight text-purple">
                      {item.name || 'Item'}
                    </span>
                  </li>
                ))}
              </ul>
              {itemsScrollable && (
                <ChevronDown
                  size={16}
                  aria-hidden
                  className="mx-auto animate-bounce text-purple2"
                />
              )}
            </div>
          ) : null}
        </StatusState>
      )}

      {status === 'failed' && (
        <StatusState
          icon={<AlertTriangle />}
          title="Payment couldn't be completed"
          subtitle="We couldn't complete your order and any charge will be refunded. Please try again or contact support."
          action={
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={goToMenu}
                className="mt-1 rounded-full bg-purple px-6 py-3 text-sm font-semibold text-white transition-colors active:bg-purple/90"
              >
                Back to menu
              </button>
              {ticketsButton('secondary')}
            </div>
          }
        />
      )}

      {status === 'timeout' && (
        <StatusState
          icon={<Clock />}
          title="Your order is still being confirmed"
          subtitle="This is taking longer than usual. Check your email for your receipt — if it doesn't arrive shortly, contact support."
          action={ticketsButton('primary')}
        />
      )}
    </main>
  )
}
